import { FC, useCallback, useState } from 'react';
import { Box, Typography } from '@mui/material';
import { useDropzone } from 'react-dropzone';

import CloudIcon from '../../assets/icons/cloud-upload-outlined.png';

import styles from './ImageUploader.module.scss';

interface ImageUploaderProps {
  image: File | null;
  onImageChange: (file: File | null) => void;
  maxFileSize?: number;
  placeholder?: {
    title?: string;
    subtitle?: string;
  };
  className?: string;
  disabled?: boolean;
}

const ImageUploader: FC<ImageUploaderProps> = ({
  image,
  onImageChange,
  maxFileSize = 5 * 1024 * 1024,
  placeholder = {
    title: 'Select your image or drag and drop',
    subtitle: 'png, jpg, gif accepted',
  },
  className = '',
  disabled = false,
}) => {
  const [preview, setPreview] = useState<string | null>(null);

  const generatePreview = useCallback((file: File) => {
    const previewUrl = URL.createObjectURL(file);
    setPreview(previewUrl);
    return () => URL.revokeObjectURL(previewUrl);
  }, []);

  const onDrop = useCallback(
    (acceptedFiles: File[]) => {
      if (disabled || acceptedFiles.length === 0) return;

      const file = acceptedFiles[0];

      if (file.size > maxFileSize) {
        console.warn(`File ${file.name} is too large. Max size: ${maxFileSize / 1024 / 1024}MB`);
        return;
      }

      generatePreview(file);
      onImageChange(file);
    },
    [onImageChange, maxFileSize, disabled, generatePreview]
  );

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'image/*': ['.png', '.jpg', '.jpeg', '.gif'],
    },
    multiple: false,
    disabled,
  });

  const handleRemoveImage = () => {
    if (preview) {
      URL.revokeObjectURL(preview);
      setPreview(null);
    }
    onImageChange(null);
  };

  return (
    <Box className={`${styles.imageUploader} ${className}`}>
      <Box
        {...getRootProps()}
        className={`${styles.dropzone} ${isDragActive ? styles.dragActive : ''} ${disabled ? styles.disabled : ''}`}
      >
        <input {...getInputProps()} />

        {image && (preview || image) ? (
          <Box className={styles.previewContainer}>
            <img
              src={preview || (image instanceof File ? URL.createObjectURL(image) : '')}
              alt="Preview"
              className={styles.previewImage}
            />
            <Box className={styles.previewOverlay}>
              <Typography
                variant="h12"
                sx={{ color: 'white' }}
              >
                Click to change image
              </Typography>
            </Box>
          </Box>
        ) : (
          <Box className={styles.uploadPrompt}>
            <img
              src={CloudIcon}
              alt="cloud"
              className={styles.cloudIcon}
            />
            <Typography
              variant="h12Semibold"
              gutterBottom
              sx={{ color: '#222B3A' }}
            >
              {placeholder.title}
            </Typography>
            <Typography
              variant="h13"
              sx={{ color: '#5E6A81' }}
            >
              {placeholder.subtitle}
            </Typography>
          </Box>
        )}
      </Box>

      {image && (
        <Box className={styles.imageInfo}>
          <Box className={styles.imageDetails}>
            <Typography
              variant="h11"
              className={styles.imageName}
            >
              {image.name}
            </Typography>
            <Typography
              variant="h12"
              sx={{ color: '#5E6A81' }}
            >
              {(image.size / 1024).toFixed(2)} KB
            </Typography>
          </Box>

          {!disabled && (
            <Typography
              variant="h12"
              sx={{ color: '#C8102E', cursor: 'pointer' }}
              onClick={handleRemoveImage}
              className={styles.removeButton}
            >
              Remove
            </Typography>
          )}
        </Box>
      )}
    </Box>
  );
};

export default ImageUploader;
